import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { type FormEvent, useState } from "react"
import { type AccessGrant, api } from "@/api.js"
import { Async } from "@/components/Async.js"
import { Confirm } from "@/components/Confirm.js"
import { Notice, PageHeader, Section } from "@/components/Page.js"
import { Button } from "@/components/ui/button.js"
import { Card, CardBody } from "@/components/ui/card.js"
import { Input, Label } from "@/components/ui/input.js"
import { Table, Td, Th, Tr } from "@/components/ui/table.js"
import { plural } from "@/lib/plural.js"
import { formatDate } from "./Entity.js"

/**
 * Who may open the panel, beyond being in the Workspace domain.
 *
 * **An empty list is the restriction switched off**: the domain alone decides,
 * as it did before the list existed, and the first grant switches it on. That
 * makes the first grant the dangerous one — a person who lists somebody else
 * first is locked out by their own next request — so the route refuses a first
 * entry that is not the author's own, and the form pre-fills it. Removing the
 * last entry switches the restriction off again, which the confirm says.
 *
 * Checked on every request, not only at sign-in, so a revocation is felt on
 * the person's next click rather than when a twelve-hour cookie expires.
 */
export function AccessScreen() {
  const state = useQuery({ queryKey: ["access"], queryFn: api.access })
  const session = useQuery({ queryKey: ["session"], queryFn: api.session, retry: false })
  const [adding, setAdding] = useState(false)
  const me = session.data?.email.toLowerCase() ?? null

  return (
    <div className="space-y-7">
      <PageHeader
        title="Dostęp do panelu"
        actions={
          <Button
            variant={adding ? "subtle" : "primary"}
            onClick={() => setAdding((open) => !open)}
          >
            {adding ? "Anuluj" : "Dodaj osobę"}
          </Button>
        }
      />

      <Async state={state}>
        {(list) => {
          const active = list.items.filter((item) => item.revokedAt === null)
          const past = list.items.filter((item) => item.revokedAt !== null)
          const domains = list.allowedDomains

          return (
            <div className="space-y-8">
              <Restriction count={active.length} domains={domains} />

              {adding && (
                <GrantAccess
                  // The first entry can only be the author's own — anything
                  // else locks them out — so it is typed in for them.
                  initialEmail={active.length === 0 ? (me ?? "") : ""}
                  domains={domains}
                  onGranted={() => setAdding(false)}
                />
              )}

              <Section
                title={`Z dostępem (${active.length})`}
                hint="Zmiana działa od następnego żądania tej osoby, nie od wygaśnięcia sesji."
              >
                <ActiveTable items={active} me={me} domains={domains} />
              </Section>
              <Section title="Historia" hint="Odebrane. Nic stąd nie znika.">
                <PastTable items={past} />
              </Section>
            </div>
          )
        }}
      </Async>
    </div>
  )
}

/** The rule that is in force right now, in one sentence. */
function Restriction({ count, domains }: { count: number; domains: readonly string[] }) {
  if (count === 0) {
    return (
      <Notice tone="neutral">
        Ograniczenie wyłączone — logować może się {anyone(domains)}. Włącza je pierwszy wpis na
        liście: dodaj najpierw swój adres.
      </Notice>
    )
  }
  return (
    <Notice tone="review">
      Ograniczenie włączone — logować mogą się tylko osoby z listy poniżej ({count}{" "}
      {plural(count, "osoba", "osoby", "osób")}).{" "}
      {domains.length === 0
        ? "Żadne inne konto nie wejdzie."
        : `Reszta domeny ${domains.join(", ")} nie wejdzie.`}
    </Notice>
  )
}

/**
 * Who may sign in with the list off, as words. Production always names a
 * domain; the local dev bypass may name none, and a sentence that read "z
 * domeny " would be a bug report.
 */
function anyone(domains: readonly string[]): string {
  return domains.length === 0 ? "każde konto Google" : `każdy z domeny ${domains.join(", ")}`
}

function ActiveTable({
  items,
  me,
  domains,
}: {
  items: readonly AccessGrant[]
  me: string | null
  domains: readonly string[]
}) {
  if (items.length === 0) {
    return <p className="text-sm text-muted-foreground">Brak.</p>
  }

  return (
    <Table>
      <thead>
        <tr>
          <Th>Adres</Th>
          <Th>Dodał</Th>
          <Th>Kiedy</Th>
          <Th className="text-right">Akcje</Th>
        </tr>
      </thead>
      <tbody>
        {items.map((item) => (
          <ActiveRow
            key={item.grantId}
            item={item}
            self={item.email === me}
            prompt={revokePrompt({ self: item.email === me, last: items.length === 1, domains })}
          />
        ))}
      </tbody>
    </Table>
  )
}

/**
 * What confirming will do, in the case that applies. Three cases, and the
 * middle one is the trap: removing yourself while others remain locks you out
 * on your own next request, and only another listed person can let you back.
 * The last entry is always the author's own — nobody else could be signed in
 * to remove it — and removing it switches the restriction off.
 */
export function revokePrompt({
  self,
  last,
  domains,
}: {
  self: boolean
  last: boolean
  domains: readonly string[]
}): string {
  if (last) {
    return `To ostatni wpis. Po usunięciu ograniczenie wyłącza się i logować może się ${anyone(domains)}.`
  }
  if (self) {
    return "Stracisz dostęp od razu. Przywrócić go może tylko inna osoba z listy."
  }
  return "Odebrać dostęp? Osoba traci go przy następnym żądaniu."
}

/**
 * One person's row. While it asks to confirm, the sentence takes the width of
 * every column but the address — it is a sentence, and squeezed into the
 * actions column it wrapped into a ladder on a narrow screen.
 */
function ActiveRow({ item, self, prompt }: { item: AccessGrant; self: boolean; prompt: string }) {
  const queryClient = useQueryClient()
  const [confirming, setConfirming] = useState(false)
  const revoke = useMutation({
    mutationFn: () => api.revokeAccess(item.grantId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["access"] })
      setConfirming(false)
    },
  })

  const address = (
    <Td className="whitespace-nowrap font-mono text-[13px] font-medium">
      {item.email}
      {self && <span className="ml-2 font-sans text-muted-foreground">(ty)</span>}
    </Td>
  )

  if (confirming) {
    return (
      <Tr>
        {address}
        <Td colSpan={3} className="text-right">
          <Confirm
            prompt={prompt}
            pending={revoke.isPending}
            error={revoke.error}
            onConfirm={() => revoke.mutate()}
            onCancel={() => setConfirming(false)}
          />
        </Td>
      </Tr>
    )
  }

  return (
    <Tr>
      {address}
      <Td className="text-muted-foreground">{item.grantedBy}</Td>
      <Td className="whitespace-nowrap font-mono text-[13px] text-muted-foreground">
        {formatDate(item.grantedAt)}
      </Td>
      <Td className="whitespace-nowrap text-right">
        <Button size="sm" variant="ghost" onClick={() => setConfirming(true)}>
          Usuń
        </Button>
      </Td>
    </Tr>
  )
}

function PastTable({ items }: { items: readonly AccessGrant[] }) {
  if (items.length === 0) {
    return <p className="text-sm text-muted-foreground">Brak.</p>
  }

  return (
    <Table>
      <thead>
        <tr>
          <Th>Adres</Th>
          <Th>Dodał</Th>
          <Th>Od</Th>
          <Th>Odebrał</Th>
          <Th>Do</Th>
        </tr>
      </thead>
      <tbody>
        {items.map((item) => (
          <Tr key={item.grantId}>
            <Td className="whitespace-nowrap font-mono text-[13px]">{item.email}</Td>
            <Td className="text-muted-foreground">{item.grantedBy}</Td>
            <Td className="whitespace-nowrap font-mono text-[13px] text-muted-foreground">
              {formatDate(item.grantedAt)}
            </Td>
            <Td className="text-muted-foreground">{item.revokedBy}</Td>
            <Td className="whitespace-nowrap font-mono text-[13px] text-muted-foreground">
              {formatDate(item.revokedAt)}
            </Td>
          </Tr>
        ))}
      </tbody>
    </Table>
  )
}

/**
 * The grant form. One field: the route takes the author from the session and
 * refuses what would do nothing — an address outside the domain, which the
 * list narrows and never widens — or would lock the author out.
 */
function GrantAccess({
  initialEmail,
  domains,
  onGranted,
}: {
  initialEmail: string
  domains: readonly string[]
  onGranted: () => void
}) {
  const queryClient = useQueryClient()
  const [email, setEmail] = useState(initialEmail)

  const grant = useMutation({
    mutationFn: api.grantAccess,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["access"] })
      onGranted()
    },
  })

  function submit(event: FormEvent) {
    event.preventDefault()
    const address = email.trim()
    if (address.length > 0) grant.mutate(address)
  }

  return (
    <Card>
      <CardBody>
        <form onSubmit={submit} className="space-y-4">
          <div className="max-w-md">
            <Label className="mb-1.5 block" htmlFor="grant-email">
              Adres e-mail
            </Label>
            <Input
              id="grant-email"
              type="email"
              className="font-mono text-[13px]"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="imie.nazwisko@packhelp.com"
              autoComplete="off"
            />
            <p className="mt-1.5 text-[13px] text-muted-foreground">
              Konto Google Workspace
              {domains.length === 0 ? "" : `, tylko z domeny ${domains.join(", ")}`}. Dostęp działa
              od zaraz.
            </p>
          </div>

          {grant.error !== null && grant.error !== undefined && (
            <p className="text-sm text-critical">
              {grant.error instanceof Error ? grant.error.message : "Nie udało się dodać."}
            </p>
          )}

          <Button type="submit" disabled={grant.isPending}>
            {grant.isPending ? "Dodaję…" : "Dodaj"}
          </Button>
        </form>
      </CardBody>
    </Card>
  )
}
